import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MailIcon, LoaderIcon, ArrowLeftIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
export const TeacherEmailVerificationPage = () => {
  const {
    currentUser,
    sendVerificationEmail,
    refreshUser
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  // Check if user is already verified
  useEffect(() => {
    const checkVerification = async () => {
      if (!currentUser) {
        navigate('/teacher-login');
        return;
      }
      try {
        await refreshUser();
        if (currentUser.emailVerified) {
          // Already verified, redirect to dashboard
          navigate('/teacher-dashboard', {
            replace: true
          });
        }
      } catch (err) {
        console.error('Error checking verification:', err);
      }
    };
    checkVerification();
  }, [currentUser, refreshUser, navigate]);
  // Initialize countdown from localStorage on mount
  useEffect(() => {
    if (!currentUser?.email) return;
    const storageKey = `lastVerificationEmailSent_${currentUser.email}`;
    const lastSentStr = localStorage.getItem(storageKey);
    if (lastSentStr) {
      const lastSent = parseInt(lastSentStr, 10);
      const timeSinceLastSend = Date.now() - lastSent;
      const cooldownMs = 60000; // 60 seconds
      const remainingMs = cooldownMs - timeSinceLastSend;
      if (remainingMs > 0) {
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        setCountdown(remainingSeconds);
      }
    }
  }, [currentUser]);
  // Countdown timer for resend button
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);
  const handleResendVerification = async () => {
    if (countdown > 0 || loading) return;
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await sendVerificationEmail();
      setSuccessMessage('Verification email sent again.');
      setCountdown(60);
    } catch (err: any) {
      console.error('Error sending verification email:', err);
      // Display user-friendly error messages
      if (err.message && err.message.includes('wait')) {
        // Custom cooldown message from AuthContext
        setError(err.message);
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  if (!currentUser) {
    return null;
  }
  return <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12" data-id="element-8670">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full" data-id="element-8671">
        <div className="text-center mb-6" data-id="element-8672">
          <div className="bg-blue-50 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center" data-id="element-8673">
            <MailIcon className="h-10 w-10 text-blue-600" data-id="element-8674" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-8675">
            Verify your email
          </h1>
          <p className="text-gray-600 text-sm mb-3" data-id="element-8676">
            We sent a verification link to{' '}
            <span className="font-medium text-gray-900" data-id="element-8677">
              {currentUser.email}
            </span>
            . You should receive it within 1–3 minutes.
          </p>
          <p className="text-gray-600 text-sm" data-id="element-8678">
            <strong className="font-semibold text-gray-700" data-id="element-8679">
              Don't see it?
            </strong>{' '}
            Please check your Spam or Promotions folder.
          </p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start" data-id="element-8680">
            <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-8681" />
            <p className="text-red-800 text-sm" data-id="element-8682">{error}</p>
          </div>}

        {successMessage && <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-start" data-id="element-8683">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-8684" />
            <p className="text-green-800 text-sm" data-id="element-8685">{successMessage}</p>
          </div>}

        <div className="space-y-3" data-id="element-8686">
          <button onClick={handleResendVerification} disabled={loading || countdown > 0} className="w-full bg-white hover:bg-gray-50 border-2 border-gray-300 disabled:border-gray-200 disabled:cursor-not-allowed disabled:opacity-60 text-gray-700 disabled:text-gray-400 py-3 px-6 rounded-lg transition-colors flex items-center justify-center font-medium" data-id="element-8687">
            {loading ? <>
                <LoaderIcon className="h-5 w-5 animate-spin mr-2" data-id="element-8688" />
                Sending...
              </> : <>
                <MailIcon className="h-5 w-5 mr-2" data-id="element-8689" />
                Resend verification email
              </>}
          </button>

          {countdown > 0 && !loading && <p className="text-sm text-gray-500 text-center" data-id="element-8690">
              You can resend in {countdown} seconds
            </p>}

          <Link to="/teacher-login" className="block text-center text-gray-600 hover:text-gray-900 text-sm flex items-center justify-center pt-2" data-id="element-8691">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-8692" />
            Back to login
          </Link>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-8693">
          <p className="text-xs text-gray-500 text-center" data-id="element-8694">
            Click the link in the email to verify your account. You can close
            this page after clicking the link.
          </p>
        </div>
      </div>
    </div>;
};